import type { StatusBadgeStatus } from '@xxtract/ui';
import type { TreeServiceView } from './orders.api';

export interface BadgeSpec {
  status: StatusBadgeStatus;
  labelKey: string;
  labelParams?: Record<string, unknown>;
}

/**
 * FR17/FR20: the "actuele processtap" badge. Never derives or invents a step when there is no
 * process data — a service without a progress definition always renders the explicit
 * "geen procesdata" badge (FR20), never a guessed status.
 */
export function processStepBadge(view: Pick<TreeServiceView, 'state' | 'progress'>): BadgeSpec {
  if (view.progress == null) return { status: 'process-no-data', labelKey: 'orders.processStep.noData' };
  if (view.state === 'onhold') return { status: 'process-on-hold', labelKey: 'orders.processStep.onhold' };
  if (view.state === 'completed' || (view.progress.currentStepNumber != null && view.progress.currentStepNumber >= view.progress.totalSteps && view.progress.totalSteps > 0)) {
    return { status: 'process-complete', labelKey: 'orders.processStep.complete' };
  }
  if (view.progress.currentStepNumber == null || view.progress.currentStepNumber === 0) {
    return { status: 'process-future', labelKey: 'orders.processStep.notStarted' };
  }
  return {
    status: 'process-current',
    labelKey: 'orders.processStep.current',
    labelParams: { current: view.progress.currentStepNumber, total: view.progress.totalSteps },
  };
}

const SERVICE_STATUS_TO_BADGE: Record<string, StatusBadgeStatus> = {
  open: 'service-open',
  requested: 'service-in-progress',
  expected: 'service-in-progress',
  received: 'service-in-progress',
  readyforprocessing: 'service-in-progress',
  processed: 'service-completed',
  validated: 'service-completed',
  delivered: 'service-completed',
  onhold: 'service-on-hold',
  canceled: 'service-closed',
  closed: 'service-closed',
};

/** FR17: the raw "servicestatus" badge (tradeItemServices.serviceStatus vocabulary). */
export function serviceStatusBadge(serviceStatus: string | null): BadgeSpec {
  if (serviceStatus == null) return { status: 'process-no-data', labelKey: 'orders.processStep.noData' };
  return { status: SERVICE_STATUS_TO_BADGE[serviceStatus] ?? 'service-degraded', labelKey: `orders.serviceStatus.${serviceStatus}` };
}
